"use client";

import { c as _c } from "react/compiler-runtime";
import { User01 } from "@untitledui/icons";
import { type FC, type ReactNode, useState } from "react";
import { cx } from "@/utils/cx";
import { AvatarOnlineIndicator, VerifiedTick } from "./base-components";
type AvatarSize = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
export interface AvatarProps {
  size?: AvatarSize;
  className?: string;
  src?: string | null;
  alt?: string;
  /**
   * Display a contrast border around the avatar.
   */
  contrastBorder?: boolean;
  /**
   * Display a badge (i.e. company logo).
   */
  badge?: ReactNode;
  /**
   * Display a status indicator.
   */
  status?: "online" | "offline";
  /**
   * Display a verified tick icon.
   *
   * @default false
   */
  verified?: boolean;

  /**
   * The initials of the user to display if no image is available.
   */
  initials?: string;
  /**
   * An icon to display if no image is available.
   */
  placeholderIcon?: FC<{
    className?: string;
  }>;
  /**
   * A placeholder to display if no image is available.
   */
  placeholder?: ReactNode;

  /**
   * Whether the avatar should show a focus ring when the parent group is in focus.
   * For example, when the avatar is wrapped inside a link.
   *
   * @default false
   */
  focusable?: boolean;
}
const styles = {
  xxs: {
    root: "size-4 outline-[0.5px] -outline-offset-[0.5px]",
    initials: "text-xs font-semibold",
    icon: "size-3"
  },
  xs: {
    root: "size-6 outline-[0.5px] -outline-offset-[0.5px]",
    initials: "text-xs font-semibold",
    icon: "size-4"
  },
  sm: {
    root: "size-8 outline-[0.75px] -outline-offset-[0.75px]",
    initials: "text-sm font-semibold",
    icon: "size-5"
  },
  md: {
    root: "size-10 outline-1 -outline-offset-1",
    initials: "text-md font-semibold",
    icon: "size-6"
  },
  lg: {
    root: "size-12 outline-1 -outline-offset-1",
    initials: "text-lg font-semibold",
    icon: "size-7"
  },
  xl: {
    root: "size-14 outline-1 -outline-offset-1",
    initials: "text-xl font-semibold",
    icon: "size-8"
  },
  "2xl": {
    root: "size-16 outline-1 -outline-offset-1",
    initials: "text-display-xs font-semibold",
    icon: "size-8"
  }
};
export const Avatar = t0 => {
  const $ = _c(26);
  const {
    contrastBorder: t1,
    size: t2,
    src,
    alt,
    initials,
    placeholder,
    placeholderIcon: PlaceholderIcon,
    badge,
    status,
    verified,
    focusable: t3,
    className
  } = t0;
  const contrastBorder = t1 === undefined ? true : t1;
  const size = t2 === undefined ? "md" : t2;
  const focusable = t3 === undefined ? false : t3;
  const [isFailed, setIsFailed] = useState(false);
  let t4;
  if ($[0] !== PlaceholderIcon || $[1] !== alt || $[2] !== initials || $[3] !== isFailed || $[4] !== placeholder || $[5] !== size || $[6] !== src) {
    t4 = () => {
      if (src && !isFailed) {
        return <img data-avatar-img={true} className="size-full rounded-full object-cover" src={src} alt={alt} onError={() => setIsFailed(true)} />;
      }
      if (initials) {
        return <span className={cx("text-quaternary", styles[size].initials)}>{initials}</span>;
      }
      if (PlaceholderIcon) {
        return <PlaceholderIcon className={cx("text-fg-quaternary", styles[size].icon)} />;
      }
      return placeholder || <User01 className={cx("text-fg-quaternary", styles[size].icon)} />;
    };
    $[0] = PlaceholderIcon;
    $[1] = alt;
    $[2] = initials;
    $[3] = isFailed;
    $[4] = placeholder;
    $[5] = size;
    $[6] = src;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const renderMainContent = t4;
  let t5;
  if ($[8] !== badge || $[9] !== size || $[10] !== status || $[11] !== verified) {
    t5 = () => {
      if (status) {
        return <AvatarOnlineIndicator status={status} size={size === "xxs" ? "xs" : size} />;
      }
      if (verified) {
        return <VerifiedTick size={size === "xxs" ? "xs" : size} className={cx("absolute right-0 bottom-0", (size === "xxs" || size === "xs") && "-right-px -bottom-px")} />;
      }
      return badge;
    };
    $[8] = badge;
    $[9] = size;
    $[10] = status;
    $[11] = verified;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const renderBadgeContent = t5;
  const t6 = focusable && "group-outline-focus-ring group-focus-visible:outline-2 group-focus-visible:outline-offset-2";
  const t7 = contrastBorder && "outline outline-avatar-contrast-border";
  const t8 = styles[size];
  let t9;
  if ($[13] !== className || $[14] !== t6 || $[15] !== t7 || $[16] !== t8.root) {
    t9 = cx("relative inline-flex shrink-0 items-center justify-center rounded-full bg-avatar-bg outline-transparent", t6, t7, t8.root, className);
    $[13] = className;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8.root;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== renderMainContent) {
    t10 = renderMainContent();
    $[18] = renderMainContent;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== renderBadgeContent) {
    t11 = renderBadgeContent();
    $[20] = renderBadgeContent;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = <div data-avatar={true} className={t9}>{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};